/**
 * Lending protocol data utilities — ported from Vue utils/lend-data.js
 */
import { eosTool } from '@/services/blockchain/eos-tool'
import { toFixed, decdiv, decmul, decadd, decsub } from '@/lib/decimal'
import { getCoinPrice } from '@/lib/farm-data'

const TTR_PAIR_ID = 2

// ─── Data Fetching ──────────────────────────────────────────

/** Fetch lending reserves from lending.ttr */
export async function handleGetCoinData(): Promise<any[]> {
  const rows = await eosTool.getTableRows<any>({
    code: 'lending.ttr', scope: 'lending.ttr', table: 'reserves', json: true, limit: 1000,
  })
  return rows || []
}

/** Fetch oracle prices from oracle.defi */
export async function handleGetPriceData(): Promise<any[]> {
  const rows = await eosTool.getTableRows<any>({
    code: 'oracle.defi', scope: 'oracle.defi', table: 'prices', json: true, limit: 1000,
  })
  return rows || []
}

/** Get user balance for a specific token */
export async function handleGetUserBalanceBySymbol(symbol: string, contract: string, account: string): Promise<string> {
  const res = await eosTool.getCurrencyBalance(contract, account, symbol)
  return res ? res.split(' ')[0] : '0'
}

/** Get user borrow data from lending.ttr userreserves */
export async function handleGetUserLendData(account: string): Promise<any[]> {
  const rows = await eosTool.getTableRows<any>({
    code: 'lending.ttr', scope: account, table: 'userreserves', json: true, limit: 1000,
  })
  return rows || []
}

/** Get user dToken balances */
export async function handleGetUserDToken(account: string, coin?: string): Promise<any> {
  const rows = await eosTool.getTableRows<any>({
    code: 'dtoken.ttr', scope: account, table: 'accounts', json: true, limit: 1000,
  })
  if (coin) {
    return rows.find((item: any) => item.balance.split(' ')[1] === 'D' + coin) || null
  }
  return rows
}

/** Get dToken stat info */
export async function handleGetCoinDToken(scope: string): Promise<any> {
  const rows = await eosTool.getTableRows<any>({
    code: 'dtoken.ttr', scope, table: 'stat', json: true, limit: 1000,
  })
  return rows.find((item: any) => item.supply.split(' ')[1] === scope) || null
}

/** Get user collateral configuration */
export async function handleGetUserCoinStakeStatus(account: string): Promise<any[]> {
  const rows = await eosTool.getTableRows<any>({
    code: 'lending.ttr', scope: account, table: 'userconfigs', json: true, limit: 1000,
  })
  return rows || []
}

// ─── Data Formatting ──────────────────────────────────────────

/** Format user collateral status */
export async function hadleFormatUserCoinStatkeStatus(list: any[], account: string): Promise<any[]> {
  const res = await handleGetUserCoinStakeStatus(account)
  if (res.length > 0) {
    for (const item of list) {
      item.use_as_collateral = 0
      for (const cfg of res) {
        if (cfg.reserve_id === item.id) {
          item.use_as_collateral = cfg.use_as_collateral
        }
      }
    }
  }
  return list
}

/** Format market list with prices and metadata */
export function handleFormatCoinList(list: any[], priceList: any[], oldList: any[]): any[] {
  for (const item of list) {
    const s = oldList.find((g: any) => g.id === item.id)
    const o = priceList.find((i: any) => i.contract === item.contract && i.coin === item.sym.split(',')[1])

    item.symbol = item.sym.split(',')[1]
    item.decimal = parseInt(item.sym.split(',')[0])
    item.dsym = item.dsym || item.sym
    item.lsym = item.lsym || item.sym

    if (item.contract === 'tethertether') {
      item.last_price = 1; item.avg_price = 1
    } else if (item.contract === 'token.ttr') {
      item.last_price = getCoinPrice(TTR_PAIR_ID)
      item.avg_price = getCoinPrice(TTR_PAIR_ID)
    } else {
      item.last_price = (o && o.last_price / 10 ** o.precision) || 0
      item.avg_price = (o && o.avg_price / 10 ** o.precision) || 0
    }

    item.lastBalance = decsub(
      item.practical_balance.split(' ')[0],
      item.total_borrows_variable.split(' ')[0],
    )

    // Carry over user state from previous list
    item.walletBalance = s?.walletBalance || 0
    item.depositBalance = s?.depositBalance || 0
    item.exchangeRate = s?.exchangeRate || 1
    item.use_as_collateral = s?.use_as_collateral ?? 1
    item.dailyEarnings = s?.dailyEarnings || 0
    item.borrowCompound = s?.borrowCompound || 0
    item.user_last_variable_borrow_cumulative_index = s?.user_last_variable_borrow_cumulative_index || ''
    item.user_last_update_time = s?.user_last_update_time || ''
    item.principal_borrow_balance = s?.principal_borrow_balance || ''
    item.compounded_interest = s?.compounded_interest || ''
    item.compounded_borrow_balance = s?.compounded_borrow_balance || ''
    item.apr = s?.apr || 0
    item.borrowApr = s?.borrowApr || 0
  }
  return list
}

// ─── Interest Calculations ──────────────────────────────────

/**
 * Calculate compounded borrow interest using power formula.
 * index = (prev_index / 1e14 * newInterest) * 1e14
 * newInterest = (borrowRate / 1e14 / (365*86400) + 1) ^ elapsedSeconds
 */
export function handleGetLastCompoundedInterest(item: any, nowSecond: number) {
  let newInterest = 0
  let newVariableIndex = 0
  let interest = 0

  if (item.current_variable_borrow_rate !== '0' && item.current_variable_borrow_rate !== 0) {
    const ratePerSecond = parseFloat(decdiv(decdiv(item.current_variable_borrow_rate, 1e14), decmul(365, 86400)))
    const lastUpdate = Math.floor(new Date(item.last_update_time + 'Z').getTime() / 1000)
    const elapsed = nowSecond - lastUpdate
    newInterest = Math.pow(ratePerSecond + 1, elapsed)
  }

  newVariableIndex = parseInt(String(
    parseFloat(decmul(decmul(decdiv(parseFloat(item.last_variable_borrow_cumulative_index), 1e14), newInterest), 1e14))
  ))

  interest = parseFloat(decmul(
    decdiv(newVariableIndex, item.user_last_variable_borrow_cumulative_index || 1),
    parseFloat(item.principal_borrow_balance || 0),
  ))

  if (interest > 0 && toFixed(interest, item.decimal) === String(parseFloat(item.principal_borrow_balance))) {
    interest = parseFloat(decadd(interest, parseFloat(decdiv(1, Math.pow(10, item.decimal)))))
  }

  return {
    newInterest: parseFloat(decadd(
      decsub(interest, parseFloat(item.principal_borrow_balance || 0)),
      parseFloat(item.compounded_interest || 0),
    )),
    borrowCompound: parseFloat(String(interest || 0)),
  }
}

/** Calculate deposit interest (linear). */
export function handleGetLastDepositInterest(item: any, nowSecond: number) {
  const ratePerSecond = parseFloat(decdiv(decdiv(item.current_liquidity_rate, 1e14), decmul(365, 86400)))
  const lastUpdate = Math.floor(new Date(item.last_update_time + 'Z').getTime() / 1000)
  const elapsed = nowSecond - lastUpdate
  const newInterest = parseFloat(decmul(ratePerSecond * elapsed, parseFloat(item.practical_balance)))
  const interestPrincipal = parseFloat(decadd(newInterest, parseFloat(item.practical_balance)))

  return { interest: newInterest, interestPrincipal }
}

/** Calculate dToken exchange rate */
export async function handleGetDTokenExchange(coinInfo: any, nowSecond: number): Promise<number> {
  const coinDToken = await handleGetCoinDToken('D' + coinInfo.symbol)
  if (!coinDToken) return 1
  const { interestPrincipal } = handleGetLastDepositInterest(coinInfo, nowSecond)
  const res = parseFloat(decdiv(interestPrincipal, parseFloat(coinDToken.supply)))
  return res || 1
}

// ─── Health Factor ──────────────────────────────────────────

/**
 * Health factor = sum(collateral_value * liquidation_threshold) / total_borrowed_value
 * type=1 uses liquidation_threshold, type=2 uses base_ltv_as_collateral
 */
export function handleGetHealthFactors(list: any[], type = 1): number {
  let totalDeposits = 0
  let totalLoans = 0

  for (const item of list) {
    if (item.avg_price && item.use_as_collateral) {
      const threshold = type === 1 ? item.liquidation_threshold : item.base_ltv_as_collateral
      totalDeposits += parseFloat(toFixed(
        parseFloat(decmul(
          parseFloat(decmul(parseFloat(item.depositBalance), parseFloat(item.avg_price))),
          parseFloat(decdiv(threshold, 10000)),
        )), 4,
      ))
    }
    const borrowVal = item.borrowCompound || parseFloat(item.principal_borrow_balance || 0)
    totalLoans += parseFloat(String(parseFloat(decmul(parseFloat(String(borrowVal)), item.avg_price))))
  }

  if (totalLoans > 0) {
    return parseFloat(parseFloat(decdiv(totalDeposits, totalLoans)).toFixed(2))
  }
  return 0
}

/** Get collateral value of a coin */
export function handleGetCoinStakeValue(item: any, isDeposit = false): number {
  if (item.avg_price && (item.use_as_collateral || isDeposit)) {
    return parseFloat(item.depositBalance) || 0
  }
  return 0
}

/** Calculate daily earnings from deposit + borrow APR */
export function handleGetDailyEarn(item: any): number {
  const depositEarn = parseFloat(decdiv(
    decmul(
      decmul(parseFloat(item.depositBalance), decadd(decdiv(item.current_liquidity_rate, 1e14), item.apr)),
      parseFloat(item.avg_price),
    ), 365,
  ))
  const borrowEarn = parseFloat(decdiv(
    decmul(
      decmul(parseFloat(item.borrowCompound), decsub(item.borrowApr, decdiv(item.current_variable_borrow_rate, 1e14))),
      parseFloat(item.avg_price),
    ), 365,
  ))
  return parseFloat(decadd(depositEarn, borrowEarn))
}

/** Format user info totals (deposits, loans, pledge, earnings) */
export function handleFormatUserInfo(list: any[]) {
  let totalDeposits = 0
  let totalLoans = 0
  let totalPledge = 0
  let pledgeRate = 0
  let everydayEarn = 0

  for (const item of list) {
    if (item.avg_price) {
      totalDeposits += parseFloat(decmul(parseFloat(item.depositBalance), parseFloat(item.avg_price)))
      totalLoans += parseFloat(decmul(parseFloat(item.borrowCompound), parseFloat(item.avg_price)))
      everydayEarn += handleGetDailyEarn(item)
    }
    if (item.avg_price && item.use_as_collateral) {
      totalPledge += parseFloat(decmul(parseFloat(item.depositBalance), parseFloat(item.avg_price)))
      pledgeRate += parseFloat(decmul(
        decmul(parseFloat(item.depositBalance), parseFloat(item.avg_price)),
        parseFloat(decdiv(item.base_ltv_as_collateral, 10000)),
      ))
    }
  }

  return { totalDeposits, totalLoans, totalPledge, pledgeRate, everydayEarn }
}
